import { DocumentList, type DocumentListResponse } from "@/components/accounting/document-list";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields } from "@/lib/search";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

/** `/accounting/expenses` — Expenses. See the shared list for what a row shows. */

const searchSchema = z.object({
  q: z.string().optional(),
  filter: z.enum(["all", "unpaid", "overdue", "paid", "draft", "cancelled"]).optional(),
  ...paginationSearchFields,
});

export const Route = createFileRoute("/_app/accounting/expenses/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    filter: search.filter,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams({ side: "received" });
    if (deps.q) params.set("q", deps.q);
    if (deps.filter) params.set("filter", deps.filter);
    if (deps.page) params.set("page", String(deps.page));
    if (deps.pageSize) params.set("pageSize", String(deps.pageSize));
    try {
      return await api.get<DocumentListResponse>(`/api/accounting/documents?${params}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: Page,
});

function Page() {
  const data = Route.useLoaderData();
  const search = Route.useSearch();

  return (
    <DocumentList
      title="Expenses"
      side="received"
      data={data}
      basePath="/accounting/expenses"
      storageKey="accounting-expenses"
      search={{ q: search.q, filter: search.filter }}
      emptyState="No expenses yet — they arrive from Netlion Scanner, from the Fakturoid import, or by hand."
    />
  );
}
